import { useState } from 'react'
import { Plus, Ribbon } from 'lucide-react'
import type { Donnee } from '../../lib/types'
import { Carte, EtatVide, Message } from '../base'
import LigneDonnee from './LigneDonnee'
import FormulaireDonnee from './FormulaireDonnee'
import { COULEURS_MODE } from '../../lib/couleurs'
import { rangChampOncologie } from '@doc/shared'

/**
 * La carte « Cancer » des dossiers de chirurgie oncologique : le diagnostic
 * de la maladie et ses caractéristiques — histologie, stade, grade…
 *
 * Elle n'invente rien : elle rassemble ce qui a été validé ailleurs, et chaque
 * ligne garde sa source. Les champs d'oncologie ne sont pas imposés, ils sont
 * suggérés à la saisie (cahier des charges, section 5).
 */
export default function Cancer({
  patientId,
  diagnostics,
  caracteristiques,
}: {
  patientId: string
  diagnostics: Donnee[]
  caracteristiques: Donnee[]
}) {
  const [ajout, setAjout] = useState<null | 'diagnostic' | 'caracteristique'>(null)

  const rangees = [...caracteristiques].sort(
    (a, b) => rangChampOncologie(a.libelle) - rangChampOncologie(b.libelle),
  )

  return (
    <Carte
      titre="Cancer"
      icone={<Ribbon className="h-[17px] w-[17px]" style={{ color: COULEURS_MODE.ONCOLOGIE.accent }} />}
      actions={
        <button
          className="bouton-discret px-2"
          onClick={() => setAjout('caracteristique')}
          title="Ajouter une caractéristique"
        >
          <Plus className="h-4 w-4" />
        </button>
      }
    >
      {diagnostics.length === 0 && rangees.length === 0 ? (
        <div className="px-5 pb-5">
          <Message ton="info">
            Rien d’enregistré pour l’instant. Le diagnostic de cancer arrive avec les diagnostics
            du dossier ; l’histologie, le stade et le grade se saisissent ici, ou sont proposés
            quand un compte rendu d’anatomo-pathologie est analysé.
          </Message>
          <div className="mt-3 flex gap-2">
            <button className="bouton-doux" onClick={() => setAjout('diagnostic')}>
              <Plus className="h-4 w-4" />
              Diagnostic
            </button>
            <button className="bouton-doux" onClick={() => setAjout('caracteristique')}>
              <Plus className="h-4 w-4" />
              Histologie, stade…
            </button>
          </div>
        </div>
      ) : (
        <>
          {diagnostics.length > 0 && (
            <ul className="pb-1">
              {diagnostics.map((donnee) => (
                <LigneDonnee key={donnee.id} donnee={donnee} patientId={patientId} />
              ))}
            </ul>
          )}

          {rangees.length > 0 && (
            <ul className="border-t border-trait pb-2">
              {rangees.map((donnee) => (
                <LigneDonnee key={donnee.id} donnee={donnee} patientId={patientId} />
              ))}
            </ul>
          )}

          {diagnostics.length === 0 && (
            <div className="px-5 pb-4">
              <EtatVide texte="Aucun diagnostic de cancer enregistré dans ce dossier." />
            </div>
          )}
        </>
      )}

      {ajout === 'caracteristique' && (
        <FormulaireDonnee
          patientId={patientId}
          categorie="DONNEE_SPECIFIQUE"
          valeursInitiales={{ domaine: 'oncologie' }}
          fermer={() => setAjout(null)}
        />
      )}
      {ajout === 'diagnostic' && (
        <FormulaireDonnee
          patientId={patientId}
          categorie="DIAGNOSTIC"
          fermer={() => setAjout(null)}
        />
      )}
    </Carte>
  )
}
